/**
 * Sugar Town Commerce — Inventory Availability (read seam).
 *
 * Stages 3–5. Normalized, provider-agnostic READ contract for product /
 * variant stock availability. Storefront surfaces (product page, cart, shop
 * cards, search) read availability through `commerceInventory` — NOT by
 * joining `inventory_quantity` off `commerce_product_variants` directly.
 *
 * Stage 5 additions:
 *   - Product-level batched availability with a deterministic aggregation rule.
 *   - Optional `inventoryPolicy` on the normalized shape.
 *   - Freshness model (`updatedAt` + `ageSeconds` + `freshness`).
 *   - Read-only Tallivo provider skeleton with contract enforcement + mock
 *     backend (disabled by default; never authoritative in production).
 *   - Optional shadow comparison against Tallivo (structured diagnostic logs).
 *
 * READ-ONLY. Never mutates carts, never reserves, never deducts, never
 * changes checkout authority. Supabase remains the enforcement source until a
 * later stage explicitly moves authority.
 */
import { supabase } from "@/integrations/supabase/client";

/** Buckets a shopper cares about. Never expose raw internal states here. */
export type CommerceInventoryStatus =
  | "in_stock"
  | "low_stock"
  | "out_of_stock"
  | "unavailable"
  | "unknown";

/** Which provider served a given availability read. */
export type CommerceInventorySourceId = "supabase" | "tallivo";

/** Normalized inventory selling policy. */
export type CommerceInventoryPolicy = "deny" | "continue" | "unknown";

/** Data-freshness bucket for diagnostics. */
export type CommerceInventoryFreshness = "fresh" | "stale" | "unknown";

/** Anything at or below this on-hand count is surfaced as "low stock". */
export const LOW_STOCK_THRESHOLD = 5;

/** Freshness cutoff — availability older than this is diagnostic-only "stale". */
export const FRESHNESS_STALE_AFTER_SECONDS = 5 * 60;

/**
 * The single normalized shape every provider returns. Nothing product- or
 * variant-specific about a warehouse leaks through this type.
 */
export interface CommerceInventoryAvailability {
  productId: string;
  variantId: string | null;
  status: CommerceInventoryStatus;
  /** Public-safe on-hand count. `null` when the provider doesn't publish it. */
  availableQuantity: number | null;
  /** Optional physical/logical location. `null` for aggregated storefront reads. */
  locationId: string | null;
  /** ISO timestamp of the last provider update, if known. */
  updatedAt: string | null;
  source: CommerceInventorySourceId;
  /** Optional — populated when the provider exposes a selling policy. */
  inventoryPolicy?: CommerceInventoryPolicy;
  /** Optional — populated when a timestamp exists. */
  ageSeconds?: number | null;
  /** Optional — derived from `updatedAt` and `FRESHNESS_STALE_AFTER_SECONDS`. */
  freshness?: CommerceInventoryFreshness;
}

/** Read contract every inventory provider must implement. */
export interface CommerceInventorySource {
  readonly id: CommerceInventorySourceId;
  getVariantAvailability(variantId: string): Promise<CommerceInventoryAvailability | null>;
  getProductAvailability(productId: string): Promise<CommerceInventoryAvailability | null>;
  getVariantAvailabilityMap(
    variantIds: string[]
  ): Promise<Map<string, CommerceInventoryAvailability>>;
  /** Batched, product-level (aggregated across sellable variants). */
  getProductsAvailabilityMap(
    productIds: string[]
  ): Promise<Map<string, CommerceInventoryAvailability>>;
}

// ---------- Normalization helpers ----------

interface VariantStockRow {
  id: string;
  product_id: string;
  inventory_quantity: number | null;
  inventory_policy: string | null;
  is_active: boolean | null;
  updated_at: string | null;
}

/**
 * Bucket a raw quantity into a shopper-facing status. `trackInventory=false`
 * means the merchant sells this without stock tracking, so it's always
 * `in_stock`. `inventory_policy='continue'` = backorder allowed and never
 * becomes a hard sold-out state.
 */
export function classifyQuantity(
  qty: number | null,
  opts: { trackInventory: boolean; policy?: string | null }
): CommerceInventoryStatus {
  if (!opts.trackInventory) return "in_stock";
  if (qty == null) return "unknown";
  if (qty <= 0) return opts.policy === "continue" ? "in_stock" : "out_of_stock";
  if (qty <= LOW_STOCK_THRESHOLD) return "low_stock";
  return "in_stock";
}

/** Normalize any raw provider policy string into the public vocabulary. */
export function normalizePolicy(raw: string | null | undefined): CommerceInventoryPolicy {
  const s = (raw ?? "").toLowerCase();
  if (s === "continue" || s === "backorder" || s === "allow") return "continue";
  if (s === "deny" || s === "block") return "deny";
  return "unknown";
}

/** Compute freshness metadata from an ISO timestamp. */
export function computeFreshness(
  updatedAt: string | null | undefined,
  now: number = Date.now()
): { ageSeconds: number | null; freshness: CommerceInventoryFreshness } {
  if (!updatedAt) return { ageSeconds: null, freshness: "unknown" };
  const t = Date.parse(updatedAt);
  if (Number.isNaN(t)) return { ageSeconds: null, freshness: "unknown" };
  const ageSeconds = Math.max(0, Math.floor((now - t) / 1000));
  return {
    ageSeconds,
    freshness: ageSeconds <= FRESHNESS_STALE_AFTER_SECONDS ? "fresh" : "stale",
  };
}

function withFreshness(a: CommerceInventoryAvailability): CommerceInventoryAvailability {
  const f = computeFreshness(a.updatedAt);
  return { ...a, ageSeconds: f.ageSeconds, freshness: f.freshness };
}

// ---------- Product-level aggregation rule ----------
//
// Deterministic v1 rule (documented + tested):
//   - in_stock:      at least one sellable variant is in_stock
//   - low_stock:     no variant is in_stock, but at least one is low_stock
//   - out_of_stock:  all known sellable variants are out_of_stock
//   - unavailable:   all variants are unavailable OR product cannot be sold
//   - unknown:       no reliable variant data
//
// We deliberately do NOT sum quantities across variants — variants may
// represent different sellable pools (size, scent, format), so summing would
// misrepresent what a customer can actually buy. `availableQuantity` is set
// only when every considered variant shares the same pool semantics, which
// today we cannot assert — so product-level `availableQuantity` is `null`.
export function aggregateVariantStatuses(
  statuses: CommerceInventoryStatus[]
): CommerceInventoryStatus {
  if (statuses.length === 0) return "unknown";
  const known = statuses.filter((s) => s !== "unknown");
  if (known.length === 0) return "unknown";
  if (known.some((s) => s === "in_stock")) return "in_stock";
  if (known.some((s) => s === "low_stock")) return "low_stock";
  if (known.every((s) => s === "unavailable")) return "unavailable";
  if (known.every((s) => s === "out_of_stock" || s === "unavailable")) return "out_of_stock";
  // Mixed unknown-with-out-of-stock etc. — safest bucket is unknown.
  return "unknown";
}

// ---------- Supabase provider (current default) ----------

const supabaseInventory: CommerceInventorySource = {
  id: "supabase",

  async getVariantAvailability(variantId) {
    const { data: variant, error } = await supabase
      .from("commerce_product_variants")
      .select("id, product_id, inventory_quantity, inventory_policy, is_active, updated_at")
      .eq("id", variantId)
      .maybeSingle();
    if (error) throw error;
    if (!variant) return null;

    const { data: product } = await supabase
      .from("commerce_products")
      .select("id, track_inventory, status")
      .eq("id", variant.product_id)
      .maybeSingle();

    const trackInventory = product?.track_inventory ?? false;
    const publishedish = product?.status === "published";
    const isActive = variant.is_active !== false && publishedish;

    if (!isActive) {
      return withFreshness({
        productId: variant.product_id,
        variantId: variant.id,
        status: "unavailable",
        availableQuantity: null,
        locationId: null,
        updatedAt: variant.updated_at ?? null,
        source: "supabase",
        inventoryPolicy: normalizePolicy(variant.inventory_policy),
      });
    }

    const qty = variant.inventory_quantity ?? null;
    return withFreshness({
      productId: variant.product_id,
      variantId: variant.id,
      status: classifyQuantity(qty, { trackInventory, policy: variant.inventory_policy }),
      availableQuantity: trackInventory ? qty : null,
      locationId: null,
      updatedAt: variant.updated_at ?? null,
      source: "supabase",
      inventoryPolicy: normalizePolicy(variant.inventory_policy),
    });
  },

  async getProductAvailability(productId) {
    const map = await this.getProductsAvailabilityMap([productId]);
    return map.get(productId) ?? null;
  },

  async getVariantAvailabilityMap(variantIds) {
    const out = new Map<string, CommerceInventoryAvailability>();
    if (variantIds.length === 0) return out;

    const { data: variants, error } = await supabase
      .from("commerce_product_variants")
      .select("id, product_id, inventory_quantity, inventory_policy, is_active, updated_at")
      .in("id", variantIds);
    if (error) throw error;

    const productIds = Array.from(new Set((variants ?? []).map((v) => v.product_id)));
    const { data: products } = productIds.length
      ? await supabase
          .from("commerce_products")
          .select("id, track_inventory, status")
          .in("id", productIds)
      : { data: [] as { id: string; track_inventory: boolean | null; status: string | null }[] };

    const productMap = new Map((products ?? []).map((p) => [p.id, p]));

    for (const v of (variants ?? []) as VariantStockRow[]) {
      const p = productMap.get(v.product_id);
      const trackInventory = p?.track_inventory ?? false;
      const publishedish = p?.status === "published";
      const isActive = v.is_active !== false && publishedish;

      if (!isActive) {
        out.set(
          v.id,
          withFreshness({
            productId: v.product_id,
            variantId: v.id,
            status: "unavailable",
            availableQuantity: null,
            locationId: null,
            updatedAt: v.updated_at ?? null,
            source: "supabase",
            inventoryPolicy: normalizePolicy(v.inventory_policy),
          })
        );
        continue;
      }

      const qty = v.inventory_quantity ?? null;
      out.set(
        v.id,
        withFreshness({
          productId: v.product_id,
          variantId: v.id,
          status: classifyQuantity(qty, { trackInventory, policy: v.inventory_policy }),
          availableQuantity: trackInventory ? qty : null,
          locationId: null,
          updatedAt: v.updated_at ?? null,
          source: "supabase",
          inventoryPolicy: normalizePolicy(v.inventory_policy),
        })
      );
    }
    return out;
  },

  async getProductsAvailabilityMap(productIds) {
    const out = new Map<string, CommerceInventoryAvailability>();
    if (productIds.length === 0) return out;

    const [{ data: products, error: pErr }, { data: variants, error: vErr }] = await Promise.all([
      supabase
        .from("commerce_products")
        .select("id, track_inventory, status")
        .in("id", productIds),
      supabase
        .from("commerce_product_variants")
        .select("id, product_id, inventory_quantity, inventory_policy, is_active, updated_at")
        .in("product_id", productIds),
    ]);
    if (pErr) throw pErr;
    if (vErr) throw vErr;

    const productMap = new Map((products ?? []).map((p) => [p.id, p]));
    const variantsByProduct = new Map<string, VariantStockRow[]>();
    for (const v of (variants ?? []) as VariantStockRow[]) {
      const arr = variantsByProduct.get(v.product_id) ?? [];
      arr.push(v);
      variantsByProduct.set(v.product_id, arr);
    }

    for (const id of productIds) {
      const product = productMap.get(id);
      if (!product) {
        out.set(
          id,
          withFreshness({
            productId: id,
            variantId: null,
            status: "unknown",
            availableQuantity: null,
            locationId: null,
            updatedAt: null,
            source: "supabase",
          })
        );
        continue;
      }
      const publishedish = product.status === "published";
      if (!publishedish) {
        out.set(
          id,
          withFreshness({
            productId: id,
            variantId: null,
            status: "unavailable",
            availableQuantity: null,
            locationId: null,
            updatedAt: null,
            source: "supabase",
          })
        );
        continue;
      }

      const trackInventory = product.track_inventory ?? false;
      const vs = (variantsByProduct.get(id) ?? []).filter((v) => v.is_active !== false);

      if (vs.length === 0) {
        out.set(
          id,
          withFreshness({
            productId: id,
            variantId: null,
            status: "unavailable",
            availableQuantity: null,
            locationId: null,
            updatedAt: null,
            source: "supabase",
          })
        );
        continue;
      }

      const statuses = vs.map((v) =>
        classifyQuantity(v.inventory_quantity ?? null, {
          trackInventory,
          policy: v.inventory_policy,
        })
      );
      const anyContinue = vs.some((v) => v.inventory_policy === "continue");
      let latest: string | null = null;
      for (const v of vs) if (v.updated_at && (!latest || v.updated_at > latest)) latest = v.updated_at;

      out.set(
        id,
        withFreshness({
          productId: id,
          variantId: null,
          status: aggregateVariantStatuses(statuses),
          // Intentionally null — variants may not share a sellable pool.
          availableQuantity: null,
          locationId: null,
          updatedAt: latest,
          source: "supabase",
          inventoryPolicy: anyContinue ? "continue" : "deny",
        })
      );
    }

    return out;
  },
};

// ---------- Tallivo provider (Stage 5 skeleton — DISABLED BY DEFAULT) ----------
//
// A read-only, contract-enforced provider that normalizes Tallivo responses
// into the same public model. In this stage there is no live Tallivo endpoint,
// so the provider is wired through an injectable `TallivoInventoryClient`.
// Production installs never call this provider unless
// `VITE_COMMERCE_INVENTORY_SOURCE=tallivo` AND a client has been wired via
// `setTallivoInventoryClient(...)`. Otherwise it falls back to Supabase.
//
// Tenant isolation: `businessId` is required on every call. Cache keys and
// mock records key by `(businessId, source)` to prevent cross-tenant leakage.
//
// Multi-location: storefront reads NEVER aggregate every location. Callers
// supply `fulfillmentLocationIds` or `onlineInventoryScope` policy; the
// provider filters records to that scope before aggregation.

export type TallivoLocationPolicy =
  | "primary_location"
  | "selected_locations"
  | "any_online_location";

export interface TallivoInventoryQuery {
  businessId: string;
  productIds?: string[];
  variantIds?: string[];
  fulfillmentLocationIds?: string[];
  onlineInventoryScope?: TallivoLocationPolicy;
}

/** Public-safe fields the Tallivo client is contractually allowed to return. */
export interface TallivoInventoryRecord {
  productId: string;
  variantId: string | null;
  locationId: string | null;
  quantity: number | null;
  policy: string | null;
  status?: CommerceInventoryStatus;
  updatedAt: string | null;
  /**
   * Whether this location is enabled for online storefront fulfillment.
   * Only records with `onlineFulfillment !== false` participate in storefront
   * aggregation. Non-fulfillment locations are silently excluded.
   */
  onlineFulfillment?: boolean;
}

export interface TallivoInventoryClient {
  fetch(query: TallivoInventoryQuery): Promise<TallivoInventoryRecord[]>;
}

/** Fields we explicitly refuse to pass through, even if a client leaks them. */
const FORBIDDEN_TALLIVO_FIELDS = new Set([
  "cost",
  "unitCost",
  "wholesalePrice",
  "supplier",
  "supplierId",
  "warehouseNote",
  "notes",
  "adjustmentReason",
  "auditTrail",
  "history",
  "valuation",
]);

/** Strip any private fields from a raw Tallivo record before it crosses the seam. */
function sanitizeTallivoRecord(raw: unknown): TallivoInventoryRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  for (const k of Object.keys(r)) {
    if (FORBIDDEN_TALLIVO_FIELDS.has(k)) delete r[k];
  }
  if (typeof r.productId !== "string") return null;
  return {
    productId: r.productId,
    variantId: typeof r.variantId === "string" ? r.variantId : null,
    locationId: typeof r.locationId === "string" ? r.locationId : null,
    quantity: typeof r.quantity === "number" ? r.quantity : null,
    policy: typeof r.policy === "string" ? r.policy : null,
    status:
      r.status === "in_stock" ||
      r.status === "low_stock" ||
      r.status === "out_of_stock" ||
      r.status === "unavailable" ||
      r.status === "unknown"
        ? r.status
        : undefined,
    updatedAt: typeof r.updatedAt === "string" ? r.updatedAt : null,
    onlineFulfillment: r.onlineFulfillment === false ? false : true,
  };
}

let tallivoClient: TallivoInventoryClient | null = null;
let tallivoBusinessId: string | null = null;
let tallivoLocationPolicy: TallivoLocationPolicy = "any_online_location";
let tallivoFulfillmentLocationIds: string[] | null = null;

/**
 * Wire a Tallivo client + tenant context. Until this is called, the Tallivo
 * provider has no way to fetch data and every method returns `unknown`
 * records, falling back to Supabase in shadow / disabled mode.
 */
export function setTallivoInventoryClient(
  client: TallivoInventoryClient | null,
  ctx?: {
    businessId?: string;
    onlineInventoryScope?: TallivoLocationPolicy;
    fulfillmentLocationIds?: string[];
  }
) {
  tallivoClient = client;
  if (ctx?.businessId) tallivoBusinessId = ctx.businessId;
  if (ctx?.onlineInventoryScope) tallivoLocationPolicy = ctx.onlineInventoryScope;
  if (ctx?.fulfillmentLocationIds) tallivoFulfillmentLocationIds = ctx.fulfillmentLocationIds;
}

function normalizeTallivoRecord(
  rec: TallivoInventoryRecord,
  trackInventory: boolean
): CommerceInventoryAvailability {
  const policy = normalizePolicy(rec.policy);
  const status: CommerceInventoryStatus =
    rec.status ??
    classifyQuantity(rec.quantity, { trackInventory, policy: rec.policy ?? null });
  return withFreshness({
    productId: rec.productId,
    variantId: rec.variantId,
    status,
    availableQuantity: trackInventory ? rec.quantity : null,
    locationId: rec.locationId,
    updatedAt: rec.updatedAt,
    source: "tallivo",
    inventoryPolicy: policy,
  });
}

function scopeRecords(records: TallivoInventoryRecord[]): TallivoInventoryRecord[] {
  const online = records.filter((r) => r.onlineFulfillment !== false);
  if (tallivoLocationPolicy === "primary_location") {
    // Without knowing the primary explicitly, we treat the first fulfillment
    // location record per (product, variant) as primary. Real integrations
    // will provide a primary flag; this is a defensive fallback.
    const seen = new Set<string>();
    return online.filter((r) => {
      const key = `${r.productId}:${r.variantId ?? ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }
  if (tallivoLocationPolicy === "selected_locations" && tallivoFulfillmentLocationIds) {
    const set = new Set(tallivoFulfillmentLocationIds);
    return online.filter((r) => r.locationId && set.has(r.locationId));
  }
  return online;
}

function unknownAvailability(
  productId: string,
  variantId: string | null,
  source: CommerceInventorySourceId
): CommerceInventoryAvailability {
  return withFreshness({
    productId,
    variantId,
    status: "unknown",
    availableQuantity: null,
    locationId: null,
    updatedAt: null,
    source,
  });
}

const tallivoInventory: CommerceInventorySource = {
  id: "tallivo",

  async getVariantAvailability(variantId) {
    if (!tallivoClient || !tallivoBusinessId) return unknownAvailability("", variantId, "tallivo");
    try {
      const raw = await tallivoClient.fetch({
        businessId: tallivoBusinessId,
        variantIds: [variantId],
        fulfillmentLocationIds: tallivoFulfillmentLocationIds ?? undefined,
        onlineInventoryScope: tallivoLocationPolicy,
      });
      const sane = raw.map(sanitizeTallivoRecord).filter((r): r is TallivoInventoryRecord => !!r);
      const scoped = scopeRecords(sane).filter((r) => r.variantId === variantId);
      if (scoped.length === 0) return unknownAvailability("", variantId, "tallivo");
      // Aggregate across matching locations (any_online_location default): take
      // the "best" status across locations so a variant available anywhere
      // sellable stays sellable to the storefront.
      const statuses = scoped.map((r) =>
        r.status ?? classifyQuantity(r.quantity, { trackInventory: true, policy: r.policy })
      );
      const status = aggregateVariantStatuses(statuses);
      const latest = scoped.reduce<string | null>(
        (acc, r) => (r.updatedAt && (!acc || r.updatedAt > acc) ? r.updatedAt : acc),
        null
      );
      return withFreshness({
        productId: scoped[0].productId,
        variantId,
        status,
        availableQuantity: null, // never sum across locations
        locationId: null,
        updatedAt: latest,
        source: "tallivo",
        inventoryPolicy: normalizePolicy(scoped[0].policy),
      });
    } catch (err) {
      console.warn("[commerce/inventory] tallivo variant fetch failed", err);
      return unknownAvailability("", variantId, "tallivo");
    }
  },

  async getProductAvailability(productId) {
    const map = await this.getProductsAvailabilityMap([productId]);
    return map.get(productId) ?? null;
  },

  async getVariantAvailabilityMap(variantIds) {
    const out = new Map<string, CommerceInventoryAvailability>();
    if (variantIds.length === 0) return out;
    if (!tallivoClient || !tallivoBusinessId) {
      for (const id of variantIds) out.set(id, unknownAvailability("", id, "tallivo"));
      return out;
    }
    try {
      const raw = await tallivoClient.fetch({
        businessId: tallivoBusinessId,
        variantIds,
        fulfillmentLocationIds: tallivoFulfillmentLocationIds ?? undefined,
        onlineInventoryScope: tallivoLocationPolicy,
      });
      const sane = raw.map(sanitizeTallivoRecord).filter((r): r is TallivoInventoryRecord => !!r);
      const scoped = scopeRecords(sane);
      const byVariant = new Map<string, TallivoInventoryRecord[]>();
      for (const r of scoped) {
        if (!r.variantId) continue;
        const arr = byVariant.get(r.variantId) ?? [];
        arr.push(r);
        byVariant.set(r.variantId, arr);
      }
      for (const id of variantIds) {
        const recs = byVariant.get(id) ?? [];
        if (recs.length === 0) {
          out.set(id, unknownAvailability("", id, "tallivo"));
          continue;
        }
        out.set(id, normalizeTallivoRecord(recs[0], true));
      }
      return out;
    } catch (err) {
      console.warn("[commerce/inventory] tallivo variant map failed", err);
      for (const id of variantIds) out.set(id, unknownAvailability("", id, "tallivo"));
      return out;
    }
  },

  async getProductsAvailabilityMap(productIds) {
    const out = new Map<string, CommerceInventoryAvailability>();
    if (productIds.length === 0) return out;
    if (!tallivoClient || !tallivoBusinessId) {
      for (const id of productIds) out.set(id, unknownAvailability(id, null, "tallivo"));
      return out;
    }
    try {
      const raw = await tallivoClient.fetch({
        businessId: tallivoBusinessId,
        productIds,
        fulfillmentLocationIds: tallivoFulfillmentLocationIds ?? undefined,
        onlineInventoryScope: tallivoLocationPolicy,
      });
      const sane = raw.map(sanitizeTallivoRecord).filter((r): r is TallivoInventoryRecord => !!r);
      const scoped = scopeRecords(sane);
      const byProduct = new Map<string, TallivoInventoryRecord[]>();
      for (const r of scoped) {
        const arr = byProduct.get(r.productId) ?? [];
        arr.push(r);
        byProduct.set(r.productId, arr);
      }
      for (const id of productIds) {
        const recs = byProduct.get(id) ?? [];
        if (recs.length === 0) {
          out.set(id, unknownAvailability(id, null, "tallivo"));
          continue;
        }
        const statuses = recs.map((r) =>
          r.status ?? classifyQuantity(r.quantity, { trackInventory: true, policy: r.policy })
        );
        const latest = recs.reduce<string | null>(
          (acc, r) => (r.updatedAt && (!acc || r.updatedAt > acc) ? r.updatedAt : acc),
          null
        );
        const anyContinue = recs.some((r) => normalizePolicy(r.policy) === "continue");
        out.set(
          id,
          withFreshness({
            productId: id,
            variantId: null,
            status: aggregateVariantStatuses(statuses),
            // Never sum quantities across variants or locations.
            availableQuantity: null,
            locationId: null,
            updatedAt: latest,
            source: "tallivo",
            inventoryPolicy: anyContinue ? "continue" : "deny",
          })
        );
      }
      return out;
    } catch (err) {
      console.warn("[commerce/inventory] tallivo product map failed", err);
      for (const id of productIds) out.set(id, unknownAvailability(id, null, "tallivo"));
      return out;
    }
  },
};

// ---------- Shadow comparison ----------

export type InventoryMismatchType =
  | "status_mismatch"
  | "quantity_mismatch"
  | "missing_in_tallivo"
  | "missing_in_supabase"
  | "location_scope_mismatch"
  | "policy_mismatch"
  | "stale_tallivo"
  | "stale_supabase";

export interface InventoryDiscrepancy {
  businessId: string | null;
  productId: string;
  variantId: string | null;
  supabaseStatus: CommerceInventoryStatus | null;
  tallivoStatus: CommerceInventoryStatus | null;
  supabaseQuantity: number | null;
  tallivoQuantity: number | null;
  supabasePolicy: CommerceInventoryPolicy | null;
  tallivoPolicy: CommerceInventoryPolicy | null;
  mismatchType: InventoryMismatchType;
  comparedAt: string;
}

export function compareAvailability(
  a: CommerceInventoryAvailability | null,
  b: CommerceInventoryAvailability | null,
  key: { productId: string; variantId: string | null }
): InventoryDiscrepancy[] {
  const out: InventoryDiscrepancy[] = [];
  const now = new Date().toISOString();
  const base = {
    businessId: tallivoBusinessId,
    productId: key.productId,
    variantId: key.variantId,
    supabaseStatus: a?.status ?? null,
    tallivoStatus: b?.status ?? null,
    supabaseQuantity: a?.availableQuantity ?? null,
    tallivoQuantity: b?.availableQuantity ?? null,
    supabasePolicy: a?.inventoryPolicy ?? null,
    tallivoPolicy: b?.inventoryPolicy ?? null,
    comparedAt: now,
  };
  if (a && !b) out.push({ ...base, mismatchType: "missing_in_tallivo" });
  if (!a && b) out.push({ ...base, mismatchType: "missing_in_supabase" });
  if (a && b) {
    if (a.status !== b.status) out.push({ ...base, mismatchType: "status_mismatch" });
    if (a.availableQuantity !== b.availableQuantity)
      out.push({ ...base, mismatchType: "quantity_mismatch" });
    if ((a.inventoryPolicy ?? "unknown") !== (b.inventoryPolicy ?? "unknown"))
      out.push({ ...base, mismatchType: "policy_mismatch" });
    if ((a.locationId ?? null) !== (b.locationId ?? null))
      out.push({ ...base, mismatchType: "location_scope_mismatch" });
    if (a.freshness === "stale") out.push({ ...base, mismatchType: "stale_supabase" });
    if (b.freshness === "stale") out.push({ ...base, mismatchType: "stale_tallivo" });
  }
  return out;
}

function shadowEnabled(): boolean {
  const raw =
    typeof import.meta !== "undefined" &&
    (import.meta as { env?: Record<string, string> }).env?.VITE_COMMERCE_INVENTORY_SHADOW_COMPARE;
  return String(raw ?? "false").toLowerCase() === "true";
}

/**
 * Structured console log for local debugging. Persistence is opt-in via
 * `setShadowDiscrepancySink(...)`; the storefront wires the edge-function
 * writer at boot when shadow mode is enabled.
 */
let discrepancySink: ((list: InventoryDiscrepancy[]) => void) | null = null;

export function setShadowDiscrepancySink(fn: ((list: InventoryDiscrepancy[]) => void) | null) {
  discrepancySink = fn;
}

function logDiscrepancies(list: InventoryDiscrepancy[]) {
  if (list.length === 0) return;
  console.info("[commerce/inventory] shadow discrepancies", list);
  try { discrepancySink?.(list); } catch (err) { console.warn("[shadow-sink] failed", err); }
}

/**
 * Wrap an active provider so every read is compared against Tallivo in the
 * background. Storefront never sees Tallivo output when this wrapper is used —
 * the returned value is always the primary provider's response.
 */
function withShadowCompare(primary: CommerceInventorySource): CommerceInventorySource {
  if (!shadowEnabled() || primary.id === "tallivo") return primary;
  const shadow = tallivoInventory;
  return {
    id: primary.id,
    getVariantAvailability: async (id) => {
      const [p, s] = await Promise.all([
        primary.getVariantAvailability(id),
        shadow.getVariantAvailability(id).catch(() => null),
      ]);
      logDiscrepancies(
        compareAvailability(p, s, { productId: p?.productId ?? "", variantId: id })
      );
      return p;
    },
    getProductAvailability: async (id) => {
      const [p, s] = await Promise.all([
        primary.getProductAvailability(id),
        shadow.getProductAvailability(id).catch(() => null),
      ]);
      logDiscrepancies(compareAvailability(p, s, { productId: id, variantId: null }));
      return p;
    },
    getVariantAvailabilityMap: async (ids) => {
      const [p, s] = await Promise.all([
        primary.getVariantAvailabilityMap(ids),
        shadow.getVariantAvailabilityMap(ids).catch(() => new Map()),
      ]);
      for (const id of ids) {
        logDiscrepancies(
          compareAvailability(p.get(id) ?? null, s.get(id) ?? null, {
            productId: p.get(id)?.productId ?? "",
            variantId: id,
          })
        );
      }
      return p;
    },
    getProductsAvailabilityMap: async (ids) => {
      const [p, s] = await Promise.all([
        primary.getProductsAvailabilityMap(ids),
        shadow.getProductsAvailabilityMap(ids).catch(() => new Map()),
      ]);
      for (const id of ids) {
        logDiscrepancies(
          compareAvailability(p.get(id) ?? null, s.get(id) ?? null, {
            productId: id,
            variantId: null,
          })
        );
      }
      return p;
    },
  };
}

// ---------- Provider selector ----------

/**
 * Explicit, opt-in provider switch. Reads `VITE_COMMERCE_INVENTORY_SOURCE`.
 * `tallivo` requires a client wired via `setTallivoInventoryClient(...)`;
 * otherwise the selector falls back to Supabase with a console warning so
 * production cannot silently flip authority.
 */
function resolveInventorySource(): CommerceInventorySource {
  const raw =
    (typeof import.meta !== "undefined" &&
      (import.meta as { env?: Record<string, string> }).env?.VITE_COMMERCE_INVENTORY_SOURCE) ||
    "supabase";
  const choice = String(raw).toLowerCase();
  if (choice === "tallivo") {
    if (!tallivoClient || !tallivoBusinessId) {
      console.warn(
        "[commerce/inventory] VITE_COMMERCE_INVENTORY_SOURCE=tallivo requested but no Tallivo client is wired; falling back to supabase."
      );
      return withShadowCompare(supabaseInventory);
    }
    return tallivoInventory;
  }
  return withShadowCompare(supabaseInventory);
}

/** The active inventory provider. Import from the adapter, not directly. */
export const commerceInventory: CommerceInventorySource = resolveInventorySource();

// ---------- Cart-line availability validation ----------

export interface CartLineForAvailability {
  itemId: string;
  productId: string;
  variantId: string;
  quantity: number;
}

export interface CartLineAvailability {
  itemId: string;
  productId: string;
  variantId: string;
  requestedQuantity: number;
  status: CommerceInventoryStatus;
  availableQuantity: number | null;
  exceedsAvailable: boolean;
  message: string | null;
  source: CommerceInventorySourceId;
}

export async function getCartAvailability(
  lines: CartLineForAvailability[]
): Promise<CartLineAvailability[]> {
  if (lines.length === 0) return [];
  const map = await commerceInventory.getVariantAvailabilityMap(
    lines.map((l) => l.variantId)
  );

  return lines.map((line) => {
    const avail = map.get(line.variantId);
    if (!avail) {
      return {
        itemId: line.itemId,
        productId: line.productId,
        variantId: line.variantId,
        requestedQuantity: line.quantity,
        status: "unknown",
        availableQuantity: null,
        exceedsAvailable: false,
        message: null,
        source: commerceInventory.id,
      };
    }
    const exceeds =
      avail.availableQuantity != null && line.quantity > avail.availableQuantity;
    return {
      itemId: line.itemId,
      productId: line.productId,
      variantId: line.variantId,
      requestedQuantity: line.quantity,
      status: exceeds && avail.status === "in_stock" ? "low_stock" : avail.status,
      availableQuantity: avail.availableQuantity,
      exceedsAvailable: exceeds,
      message: messageFor(avail.status, avail.availableQuantity, exceeds),
      source: avail.source,
    };
  });
}

function messageFor(
  status: CommerceInventoryStatus,
  qty: number | null,
  exceeds: boolean
): string | null {
  if (status === "unavailable") return "This item is currently unavailable.";
  if (status === "out_of_stock") return "This item is currently sold out.";
  if (exceeds && qty != null) return `Only ${qty} currently available.`;
  if (status === "low_stock") return qty != null ? `Only ${qty} available.` : "Low stock.";
  return null;
}

// ---------- Shopper-facing message builders ----------

export function describeVariantAvailability(
  avail: Pick<CommerceInventoryAvailability, "status" | "availableQuantity"> | null | undefined
): string | null {
  if (!avail) return null;
  const { status, availableQuantity: qty } = avail;
  if (status === "out_of_stock") return "Sold out.";
  if (status === "unavailable") return "Currently unavailable.";
  if (status === "low_stock") return qty != null ? `Only ${qty} left.` : "Low stock.";
  return null;
}

export function describeCartLineAvailability(
  line: Pick<CartLineAvailability, "status" | "availableQuantity" | "exceedsAvailable">
): string | null {
  return messageFor(line.status, line.availableQuantity, line.exceedsAvailable);
}

export function shouldBlockAddToCart(
  avail: Pick<CommerceInventoryAvailability, "status"> | null | undefined
): boolean {
  if (!avail) return false;
  return avail.status === "out_of_stock" || avail.status === "unavailable";
}

/** Small, no-quantities badge label for product cards. `null` = no badge. */
export function productCardBadge(
  status: CommerceInventoryStatus | null | undefined
): "Low stock" | "Sold out" | "Unavailable" | null {
  if (status === "low_stock") return "Low stock";
  if (status === "out_of_stock") return "Sold out";
  if (status === "unavailable") return "Unavailable";
  return null;
}
